import React, { useState } from "react";
import { X, Eye, TrendingUp, Check } from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LineChart,
  Line,
} from "recharts";
import { AppUtils } from "../../services/AppUtils";
import "./MyCoreTeam.css";

export const MemberCard = ({ member, onRemove, onViewDashboard }) => {
  const [hover, setHover] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div
      className={`my-core-team-member-card ${hover ? "my-core-team-member-card:hover" : ""}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* Header */}
      <div className="my-core-team-member-header">
        <div className="my-core-team-member-info">
          {/* Avatar as rank image */}
          <div className="my-core-team-avatar">
            {member.rankImage ? (
              <img
                src={member.rankImage}
                alt={member.currentRank}
                style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "50%" }}
                onError={(e) => {
                  // Fallback to initials if image fails to load
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
            ) : null}
            <div
              style={{
                display: member.rankImage ? "none" : "flex",
                background: "var(--primary)",
                width: "100%",
                height: "100%",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
              }}
            >
              {AppUtils.getInitials(member.name)}
            </div>
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700 }}>{member.name}</h3>
            <p style={{ margin: "4px 0 0", color: "var(--text-secondary)", fontSize: "14px" }}>
              ID: {member.id} | {member.currentRank}
            </p>
          </div>
        </div>

        <div style={{ display: "flex", gap: "8px" }}>
          <button
            className="my-core-team-button my-core-team-secondary-button"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? <X size={16} /> : <Eye size={16} />}
            {showDetails ? "Hide" : "View"} Details
          </button>
          <button
            className="my-core-team-button my-core-team-primary-button"
            onClick={() => onViewDashboard(member.id)}
          >
            <TrendingUp size={16} color="white" />
            Dashboard
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="my-core-team-grid">
        <div>
          <p style={{ margin: 0, fontSize: "12px", color: "var(--text-secondary)" }}>Personal PV</p>
          <p style={{ margin: "4px 0", fontSize: "20px", fontWeight: 700 }}>
            {AppUtils.formatNumber(member.personalPV)}
          </p>
        </div>
        <div>
          <p style={{ margin: 0, fontSize: "12px", color: "var(--text-secondary)" }}>Team Size</p>
          <p style={{ margin: "4px 0", fontSize: "20px", fontWeight: 700 }}>{AppUtils.formatNumber(member.teamSize)}</p>
        </div>
        <div>
          <p style={{ margin: 0, fontSize: "12px", color: "var(--text-secondary)" }}>Left/Right Volume</p>
          <p style={{ margin: "4px 0", fontSize: "20px", fontWeight: 700 }}>
            {AppUtils.formatNumber(member.leftVolume)}/{AppUtils.formatNumber(member.rightVolume)}
          </p>
        </div>
        <div>
          <p style={{ margin: 0, fontSize: "12px", color: "var(--text-secondary)" }}>Champions League</p>
          <div style={{ marginTop: "4px" }}>
            {member.championsLeagueQualified ? (
              <span className="my-core-team-chip my-core-team-success-chip">
                <Check size={14} color="var(--success)" />
                Qualified
              </span>
            ) : (
              <span className="my-core-team-chip my-core-team-warning-chip">Not Qualified</span>
            )}
          </div>
        </div>
        <div>
          <p style={{ margin: 0, fontSize: "12px", color: "var(--text-secondary)" }}>Join Date</p>
          <p style={{ margin: "4px 0", fontSize: "14px", fontWeight: 600 }}>{AppUtils.formatDate(member.joinDate)}</p>
        </div>
        <div>
          <p style={{ margin: 0, fontSize: "12px", color: "var(--text-secondary)" }}>Phone</p>
          <p style={{ margin: "4px 0", fontSize: "14px", fontWeight: 600 }}>{member.phone}</p>
        </div>
        <div>
          <p style={{ margin: 0, fontSize: "12px", color: "var(--text-secondary)" }}>Status</p>
          <p style={{ margin: "4px 0", fontSize: "14px", fontWeight: 600, textTransform: "capitalize" }}>
            {member.status}
          </p>
        </div>
      </div>
      {/* Next Rank Progress */}
      {/* <div style={{ marginTop: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <p style={{ margin: 0, fontSize: "14px", fontWeight: 600 }}>Progress to {member.nextRank}</p>
            <p style={{ margin: 0, fontSize: "12px", color: "var(--text-secondary)" }}>
              {formatNumber(member.remainingAggregateIncome)} GBU remaining
            </p>
          </div>
          <div className="my-core-team-progress-bar">
            <div
              className="my-core-team-progress-fill"
              style={{
                width: `${Math.min(
                  100,
                  (member.aggregateIncome / (member.aggregateIncome + member.remainingAggregateIncome)) * 100
                )}%`,
              }}
            />
          </div>
        </div> */}

      {/* Detailed View */}
      {showDetails && (
        <div style={{ marginTop: "24px", paddingTop: "24px", borderTop: "1px solid var(--divider)" }}>
          {/* Income Chart */}
          {member.weeklyIncome && member.weeklyIncome.length > 0 && (
            <div style={{ marginBottom: "24px" }}>
              <h4 style={{ margin: "0 0 16px", fontSize: "16px", fontWeight: 600 }}>Income Trend (Last 13 Weeks)</h4>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={member.weeklyIncome}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--grey-200)" />
                  <XAxis dataKey="week" stroke="var(--text-secondary)" fontSize={12} />
                  <YAxis stroke="var(--text-secondary)" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      background: "white",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(94, 92, 92, 0.1)",
                    }}
                    formatter={(value) => [AppUtils.formatNumber(value), "Income"]}
                  />
                  <Area type="monotone" dataKey="income" stroke="var(--primary)" fill="#e8f2fd" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Volume Chart */}
          {member.volumeData && member.volumeData.length > 0 && (
            <div style={{ marginBottom: "24px" }}>
              <h4 style={{ margin: "0 0 16px", fontSize: "16px", fontWeight: 600 }}>Volume Trend (Last 13 Weeks)</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={member.volumeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--grey-200)" />
                  <XAxis dataKey="week" stroke="var(--text-secondary)" fontSize={12} />
                  <YAxis stroke="var(--text-secondary)" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      background: "white",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                    formatter={(value) => [AppUtils.formatNumber(value), ""]}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="leftVolume"
                    stroke="var(--primary)"
                    strokeWidth={2}
                    name="Left Volume"
                  />
                  <Line
                    type="monotone"
                    dataKey="rightVolume"
                    stroke="var(--secondary)"
                    strokeWidth={2}
                    name="Right Volume"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <button className="my-core-team-button my-core-team-danger-button" onClick={() => onRemove(member.id)}>
            Remove from Core Team
          </button>
        </div>
      )}
    </div>
  );
};
